/*
  Firebird theme fixes (Sept 2026)
  Loaded after theme.js. Everything here is additive; removing this file
  returns the theme to stock Impulse behaviour.

  1. Variant picker: keep the size + colour the shopper tapped, even when
     that combination is sold out, so the button reads "Sold out" and the
     Klaviyo "Notify me" button can appear. Stock Impulse silently jumps to
     the closest available variant (e.g. Dark Blue -> Black).
  2. Pre-order label: products tagged "preorder" show "Pre-order" text on
     variants that are purchasable only because "continue selling when out
     of stock" is on.
  3. Mobile sticky add-to-cart bar.
  4. Free gift-note field + optional $5 gift box product added to the cart.
*/
(function () {
  'use strict';

  /* 1. Exact-match variant selection ------------------------------------ */
  if (window.theme && theme.Variants && theme.Variants.prototype) {
    theme.Variants.prototype._getVariantFromOptions = function (lastSelectedOption) {
      var availableFullMatch = this._getFullMatch(true);
      var fullMatch = this._getFullMatch(false);
      var closest = this._getClosestAvailableMatch(lastSelectedOption);
      if (this.dynamicVariantsEnabled) {
        return availableFullMatch || fullMatch || closest || null;
      }
      return fullMatch || null;
    };
  }

  /* 2. Pre-order label --------------------------------------------------- */
  var PREORDER_TEXT = (window.theme && theme.strings && theme.strings.preorderLabel) || 'Pre-order';

  // Shopify strips inventory fields from the storefront variant JSON, so the
  // product template renders the list of pre-order variant ids in Liquid
  // (<script data-preorder-variants>). Fall back to the JSON fields if present.
  function preorderIds(section) {
    var el = section && section.querySelector('[data-preorder-variants]');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '[]'); } catch (e) { return null; }
  }

  function isPreorderVariant(section, variant) {
    if (!variant || !variant.available) return false;
    var ids = preorderIds(section);
    if (ids) return ids.indexOf(Number(variant.id)) > -1;
    var qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;
    return variant.inventory_policy === 'continue' && variant.inventory_management && qty !== null && qty <= 0;
  }

  function applyPreorderLabel(section, variant) {
    if (!section || !section.querySelector('[data-preorder-enabled]')) return;
    var textEl = section.querySelector('[data-add-to-cart-text]');
    var btn = section.querySelector('[data-add-to-cart]');
    var note = section.querySelector('[data-preorder-note]');
    if (!textEl || !btn) return;
    if (isPreorderVariant(section, variant)) {
      textEl.textContent = PREORDER_TEXT;
      btn.classList.add('btn--preorder');
      if (note) note.hidden = false;
    } else {
      btn.classList.remove('btn--preorder');
      if (note) note.hidden = true;
    }
  }

  document.addEventListener('variant:change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    document.querySelectorAll('[data-section-type="product"]').forEach(function (section) {
      var sel = section.querySelector('select[name="id"]');
      if (variant && sel && String(sel.value) === String(variant.id)) applyPreorderLabel(section, variant);
    });
    syncStickyBar();
  });

  function initialPreorderCheck() {
    document.querySelectorAll('[data-section-type="product"]').forEach(function (section) {
      if (!section.querySelector('[data-preorder-enabled]')) return;
      var ta = section.querySelector('[data-current-variant-json]');
      if (!ta) return;
      try { applyPreorderLabel(section, JSON.parse(ta.textContent)); } catch (e) {}
    });
  }

  /* 3. Mobile sticky add-to-cart ---------------------------------------- */
  var bar, observer;

  function syncStickyBar() {
    if (!bar) return;
    var section = document.querySelector('[data-section-type="product"][data-history]');
    if (!section) return;
    var realBtn = section.querySelector('[data-add-to-cart]');
    var realText = section.querySelector('[data-add-to-cart-text]');
    var price = section.querySelector('[data-product-price]');
    var stickyBtn = bar.querySelector('[data-sticky-add]');
    var stickyText = bar.querySelector('[data-sticky-text]');
    var stickyPrice = bar.querySelector('[data-sticky-price]');
    if (realBtn && stickyBtn) {
      stickyBtn.disabled = realBtn.disabled;
      stickyText.textContent = realBtn.disabled ? (realText ? realText.textContent.trim() : 'Sold out') : (realText ? realText.textContent.trim() : 'Add to cart');
      if (realBtn.disabled) stickyText.textContent = realText ? realText.textContent.trim() : 'Sold out';
    }
    if (price && stickyPrice) stickyPrice.textContent = price.textContent.trim();
  }

  function initStickyBar() {
    bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;
    var section = document.querySelector('[data-section-type="product"][data-history]');
    if (!section) return;
    var form = section.querySelector('form[action*="/cart/add"]');
    var realBtn = section.querySelector('[data-add-to-cart]');
    if (!form || !realBtn) return;

    bar.querySelector('[data-sticky-add]').addEventListener('click', function () {
      if (realBtn.disabled) {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      realBtn.click();
    });

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        var pastTop = entries[0].boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', !visible && pastTop);
      }, { threshold: 0 });
      observer.observe(realBtn);
    }
    syncStickyBar();
  }

  /* 4. Gift note (free) + gift box (+$5 product added to the cart) -------- */
  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function initGiftBox() {
    document.querySelectorAll('[data-gift-box]').forEach(function (wrap) {
      var noteToggle = wrap.querySelector('[data-gift-note-toggle]');
      var noteWrap = wrap.querySelector('[data-gift-note]');
      var ta = noteWrap ? noteWrap.querySelector('textarea') : null;
      if (noteToggle && noteWrap) {
        var toggle = function () {
          noteWrap.hidden = !noteToggle.checked;
          if (ta) { ta.disabled = !noteToggle.checked; if (!noteToggle.checked) ta.value = ''; }
        };
        noteToggle.addEventListener('change', toggle);
        toggle();
      }

      var boxToggle = wrap.querySelector('[data-gift-box-toggle]');
      var status = wrap.querySelector('[data-gift-box-status]');
      var variantId = wrap.getAttribute('data-gift-box-variant');
      if (!boxToggle || !variantId) return;

      // Reflect the current cart state on load.
      fetch(theme.routes.cart, { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
        var has = (cart.items || []).some(function (i) { return String(i.variant_id) === String(variantId); });
        boxToggle.checked = has;
        if (status) { status.hidden = !has; status.textContent = has ? 'Gift box is in your cart.' : ''; }
      }).catch(function () {});

      boxToggle.addEventListener('change', function () {
        boxToggle.disabled = true;
        var req;
        if (boxToggle.checked) {
          req = cartRequest(theme.routes.cartAdd, { items: [{ id: Number(variantId), quantity: 1 }] });
        } else {
          req = cartRequest(theme.routes.cartChange, { id: String(variantId), quantity: 0 });
        }
        req.then(function () {
          if (status) { status.hidden = !boxToggle.checked; status.textContent = boxToggle.checked ? 'Gift box added to your cart.' : ''; }
          document.dispatchEvent(new CustomEvent('cart:build'));
        }).catch(function () {
          boxToggle.checked = !boxToggle.checked;
        }).then(function () { boxToggle.disabled = false; });
      });
    });
  }

  function init() {
    initialPreorderCheck();
    initStickyBar();
    initGiftBox();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('page:loaded', function () { initialPreorderCheck(); syncStickyBar(); });
})();


/*
  5. Paid-traffic popup suppression (Oct 5, 2026; selectors fixed Oct 9)
     Visitors who arrive from a paid Meta click (utm_source=meta, utm_medium=paid,
     or an fbclid) are flagged for the browser session (and 24h via cookie).
     For flagged visitors the 20% welcome sign-up forms never render, on any page.
     Any other Klaviyo form (for example a paid-only form with its own ID) is untouched.
     Remove this block to restore the popup for everyone.
*/
(function () {
  'use strict';
  var FORMS = ['RcX4Be', 'S2WS9k', 'TymSJY', 'W3ceLa']; // W3ceLa = Welcome 20% (all devices), Oct 7
  var KEY = 'fb_paid';
  var flagged = false;
  try {
    var params = new URLSearchParams(window.location.search);
    var src = (params.get('utm_source') || '').toLowerCase();
    var med = (params.get('utm_medium') || '').toLowerCase();
    var paidNow = src === 'meta' || src === 'facebook' || src === 'instagram' || src === 'ig' || src === 'fb' ||
      med === 'paid' || med === 'cpc' || med === 'paid_social' || params.has('fbclid');
    if (paidNow) {
      try { window.sessionStorage.setItem(KEY, '1'); } catch (e) {}
      document.cookie = KEY + '=1; path=/; max-age=86400; SameSite=Lax';
    }
    var fromSession = false;
    try { fromSession = window.sessionStorage.getItem(KEY) === '1'; } catch (e) {}
    flagged = paidNow || fromSession || document.cookie.indexOf(KEY + '=1') !== -1;
  } catch (e) { flagged = false; }
  if (!flagged) return;

  document.documentElement.setAttribute('data-fb-paid', '1');

  // Klaviyo marks a popup or flyout with data-testid="klaviyo-form-<ID>" and its teaser with
  // class kl-teaser-<ID>; the klaviyo-form-<ID> class exists only on embedded forms (verified Oct 9, 2026).
  var selector = FORMS.map(function (id) {
    return '[data-testid="klaviyo-form-' + id + '"], .kl-teaser-' + id + ', .klaviyo-form-' + id + ', #klaviyo-form-' + id;
  }).join(', ');

  // CSS first: hides the whole Klaviyo root for these forms (overlay and teaser included) in browsers with :has().
  var style = document.createElement('style');
  style.setAttribute('data-fb-paid-popup', '');
  style.textContent = FORMS.map(function (id) {
    return 'body > div:has([data-testid="klaviyo-form-' + id + '"]), body > div:has(.kl-teaser-' + id + '), body > div:has(.klaviyo-form-' + id + ')';
  }).join(', ') + '{display:none !important;}' +
    selector + '{display:none !important;}';
  (document.head || document.documentElement).appendChild(style);

  // JS fallback: when a flagged form mounts, hide its body-level wrapper so no overlay is left behind.
  function hideMounted() {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      while (el && el.parentElement && el.parentElement !== document.body) el = el.parentElement;
      if (el && el !== document.body) el.style.setProperty('display', 'none', 'important');
    }
  }
  if (window.MutationObserver) {
    new MutationObserver(hideMounted).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideMounted);
  } else {
    hideMounted();
  }
})();


/*
  6. Outfit pairing: "Wear it with" on product pages + cart drawer bar to the
     10% at $150 tier (Oct 5, 2026). Data comes from snippets/firebird-pairs.liquid.
     Events used from Impulse: cart:updated (detail.cart), cart:build, ajaxProduct:added.
*/
(function () {
  'use strict';
  var TIER_CENTS = 15000;
  var PANT_HANDLE = 'kids-pants-denim';
  var pairs = null;

  function money(cents) {
    var n = cents / 100;
    return '$' + (Math.abs(n - Math.round(n)) < 0.005 ? Math.round(n) : n.toFixed(2));
  }
  function loadPairs() {
    if (pairs) return pairs;
    var el = document.querySelector('script[data-fb-pairs]');
    if (!el) return null;
    try { pairs = JSON.parse(el.textContent); } catch (e) { pairs = null; }
    return pairs;
  }
  function sizeFamily(title) {
    var s = String(title || '').split(' / ')[0].trim().toUpperCase();
    if (s === '2T' || s === '3T' || s === '2-3T') return '2-3T';
    if (s === '4T' || s === '5T' || s === '4-5T') return '4-5T';
    return s;
  }
  function available(p) { return p.variants.filter(function (v) { return v.available; }); }
  function pickVariant(p, fam) {
    var avail = available(p);
    if (!avail.length) return null;
    if (fam) {
      var hit = avail.filter(function (v) { return sizeFamily(v.title) === fam; })[0];
      if (hit) return hit;
    }
    return avail[0];
  }
  function candidates(role, exclude, fam) {
    var data = loadPairs() || [];
    var list = data.filter(function (p) {
      if (p.handle === exclude) return false;
      if (role === 'bottoms') return p.handle !== PANT_HANDLE;
      return p.handle === PANT_HANDLE;
    }).filter(function (p) { return available(p).length; });
    // Prefer products that have the shopper's size in stock.
    list.sort(function (a, b) {
      var am = fam && available(a).some(function (v) { return sizeFamily(v.title) === fam; }) ? 0 : 1;
      var bm = fam && available(b).some(function (v) { return sizeFamily(v.title) === fam; }) ? 0 : 1;
      return am - bm;
    });
    return list.slice(0, 2);
  }
  var lastVariantTitle = '';
  function currentSizeFamily() {
    if (lastVariantTitle) return sizeFamily(lastVariantTitle);
    var sel = document.querySelector('[data-section-type="product"] select[name="id"]');
    if (!sel || !sel.options[sel.selectedIndex]) return '';
    return sizeFamily(sel.options[sel.selectedIndex].textContent);
  }
  function addToCart(variantId, onDone) {
    return fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); }).then(onDone);
  }
  function buildCard(p, fam, compact, afterAdd) {
    var pre = pickVariant(p, fam);
    var card = document.createElement('div');
    card.className = 'fb-card' + (compact ? ' fb-card--compact' : '');
    var img = document.createElement('a');
    img.className = 'fb-card__img'; img.href = p.url;
    var im = document.createElement('img'); im.src = p.image; im.alt = p.title; im.loading = 'lazy';
    img.appendChild(im);
    var name = document.createElement('a'); name.className = 'fb-card__name'; name.href = p.url; name.textContent = p.title;
    var price = document.createElement('div'); price.className = 'fb-card__price'; price.textContent = money(pre ? pre.price : p.price);
    var row = document.createElement('div'); row.className = 'fb-card__row';
    var sel = document.createElement('select'); sel.className = 'fb-card__size'; sel.setAttribute('aria-label', 'Size for ' + p.title);
    available(p).forEach(function (v) {
      var o = document.createElement('option'); o.value = v.id; o.textContent = v.title;
      if (pre && v.id === pre.id) o.selected = true;
      sel.appendChild(o);
    });
    sel.addEventListener('change', function () {
      var v = p.variants.filter(function (x) { return String(x.id) === sel.value; })[0];
      if (v) price.textContent = money(v.price);
    });
    var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'fb-card__add'; btn.textContent = 'Add';
    var status = document.createElement('div'); status.className = 'fb-card__status';
    btn.addEventListener('click', function () {
      btn.disabled = true; btn.textContent = 'Adding';
      addToCart(sel.value, function () {
        btn.textContent = 'Added'; status.textContent = 'In your cart.';
        afterAdd();
        setTimeout(function () { btn.disabled = false; btn.textContent = 'Add'; }, 1500);
      }).catch(function () { btn.disabled = false; btn.textContent = 'Add'; status.textContent = 'Could not add. Try again.'; });
    });
    row.appendChild(sel); row.appendChild(btn);
    if (compact) {
      var body = document.createElement('div'); body.className = 'fb-card__body';
      body.appendChild(name); body.appendChild(price); body.appendChild(row); body.appendChild(status);
      card.appendChild(img); card.appendChild(body);
    } else {
      card.appendChild(img); card.appendChild(name); card.appendChild(price); card.appendChild(row); card.appendChild(status);
    }
    return card;
  }

  /* Product page block */
  function renderWearWith() {
    var wrap = document.querySelector('[data-fb-wear]');
    if (!wrap) return;
    var role = wrap.getAttribute('data-fb-wear');
    var exclude = wrap.getAttribute('data-fb-exclude');
    var fam = currentSizeFamily();
    var list = candidates(role, exclude, fam);
    var holder = wrap.querySelector('[data-fb-wear-cards]');
    holder.innerHTML = '';
    if (!list.length) { wrap.hidden = true; return; }
    list.forEach(function (p) {
      holder.appendChild(buildCard(p, fam, false, function () {
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
        setTimeout(refreshTier, 600);
      }));
    });
    wrap.hidden = false;
  }

  /* Cart drawer: progress to the 10% tier + suggested add */
  function renderTier(cart) {
    var bar = document.querySelector('[data-fb-tier]');
    if (!bar) return;
    if (!cart || !cart.item_count) { bar.hidden = true; bar.innerHTML = ''; return; }
    var subtotal = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : cart.total_price;
    var pct = Math.min(100, Math.round(subtotal / TIER_CENTS * 100));
    var html = '';
    if (subtotal >= TIER_CENTS) {
      html += '<p class="fb-tier__text">10% off your order, applied at checkout.</p>';
      html += '<div class="fb-tier__track"><div class="fb-tier__fill" style="width:100%"></div></div>';
      bar.innerHTML = html; bar.hidden = false; return;
    }
    html += '<p class="fb-tier__text">You are ' + money(TIER_CENTS - subtotal) + ' from 10% off your order.</p>';
    html += '<div class="fb-tier__track"><div class="fb-tier__fill" style="width:' + pct + '%"></div></div>';
    html += '<div class="fb-tier__cards" data-fb-tier-cards></div>';
    bar.innerHTML = html;
    var hasPant = (cart.items || []).some(function (i) { return i.handle === PANT_HANDLE; });
    var fam = '';
    var pant = (cart.items || []).filter(function (i) { return i.handle === PANT_HANDLE; })[0];
    if (pant) fam = sizeFamily(pant.variant_title || pant.title.split(' - ')[1] || '');
    var inCart = {};
    (cart.items || []).forEach(function (i) { inCart[i.handle] = true; });
    var list = candidates(hasPant ? 'bottoms' : 'tops', '', fam).filter(function (p) { return !inCart[p.handle]; }).slice(0, 1);
    var holder = bar.querySelector('[data-fb-tier-cards]');
    list.forEach(function (p) {
      holder.appendChild(buildCard(p, fam, true, function () {
        document.dispatchEvent(new CustomEvent('cart:build'));
        setTimeout(refreshTier, 600);
      }));
    });
    bar.hidden = false;
  }
  function refreshTier() {
    if (!document.querySelector('[data-fb-tier]')) return;
    fetch(theme.routes.cart, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); }).then(renderTier).catch(function () {});
  }

  // Impulse rebuilds the drawer through CartForm.buildCart; refresh the bar after each rebuild.
  function hookCartForm() {
    var CF = window.theme && theme.CartForm;
    if (!CF || !CF.prototype || typeof CF.prototype.buildCart !== 'function' || CF.prototype.__fbTierHooked) return;
    var orig = CF.prototype.buildCart;
    CF.prototype.buildCart = function () {
      var r = orig.apply(this, arguments);
      setTimeout(refreshTier, 400);
      return r;
    };
    CF.prototype.__fbTierHooked = true;
  }

  function init() {
    hookCartForm();
    if (!loadPairs()) return;
    renderWearWith();
    refreshTier();
  }
  document.addEventListener('cart:open', function () { setTimeout(refreshTier, 50); });
  document.addEventListener('variant:change', function (evt) {
    var v = evt.detail && evt.detail.variant;
    if (v && v.title) lastVariantTitle = v.title;
    setTimeout(renderWearWith, 0);
  });
  document.addEventListener('cart:updated', function (evt) {
    if (evt.detail && evt.detail.cart) renderTier(evt.detail.cart); else refreshTier();
  });
  document.addEventListener('page:loaded', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
